/**
 * 折线图（纯 SVG，不引图表库）。
 *
 * 为什么不用 recharts 之类：本项目的依赖已经被前端包锁得很紧，加一个图表库
 * 会带来 200KB+ 的体积和一套新的样式体系；而这里只需要一条折线 + 坐标轴。
 * 手写 SVG 还能让"儿童模式隐藏图表"变成一句 `if`，而不是去覆盖第三方组件。
 *
 * 无障碍：图形本身 aria-hidden，另配一个可读的表格摘要（sr-only），
 * 读屏用户能听到每个点的值。
 */
import type { TimeSeriesPoint } from '../../lib/api/types';
import { cn } from '../../lib/utils';

export interface TimeSeriesChartProps {
  points: TimeSeriesPoint[];
  /** 数值取哪个字段。 */
  metric: 'completion' | 'duration' | 'points';
  /** 图形高度（px）。 */
  height?: number;
  className?: string;
  emptyHint?: string;
}

const WIDTH = 720; // viewBox 宽度；实际显示宽度由容器决定（preserveAspectRatio 缩放）

function valueOf(point: TimeSeriesPoint, metric: TimeSeriesChartProps['metric']): number {
  if (metric === 'completion') return point.ratio ?? 0;
  if (metric === 'duration') return point.minutes ?? 0;
  return point.points ?? 0;
}

/**
 * 折线图的刻度/摘要文案。
 *
 * ⚠️ `metric === 'duration'` 的数值是**服务端已经四舍五入到整分钟**的聚合
 * （`stats.service.ts` 里 `Math.round(sum(actual_seconds) / 60)`），也就是秒在
 * 后端就已经丢了 —— 所以这里**不能**改用 `@fht/shared` 的 `formatDuration`
 * （那是"某一次打卡的实际用时"，`'19 分 23 秒'` 那个口径）：它会把 `12` 显示成
 * `'12 分 0 秒'`，凭空多出一个并不存在的"0 秒"精度。
 *
 * 想让这张图也显示到秒，得先让 `/stats/timeseries` 返回秒级聚合（后端改动，
 * 本轮"只做显示格式"的范围之外）。**不要**在前端把分钟乘回 60 假装有秒。
 */
function formatValue(value: number, metric: TimeSeriesChartProps['metric']): string {
  if (metric === 'completion') return `${Math.round(value * 100)}%`;
  if (metric === 'duration') return `${Math.round(value)} 分`;
  return `${Math.round(value)} 分`;
}

/** 按点数稀疏化 X 轴标签，避免小屏上文字叠在一起。 */
function labelStep(count: number): number {
  if (count <= 8) return 1;
  if (count <= 16) return 2;
  if (count <= 32) return 4;
  return Math.ceil(count / 8);
}

export function TimeSeriesChart({
  points,
  metric,
  height = 200,
  className,
  emptyHint = '这一区间还没有数据',
}: TimeSeriesChartProps) {
  if (points.length === 0) {
    return (
      <div
        className={cn(
          'flex items-center justify-center rounded-xl border border-dashed border-slate-300 bg-slate-50/60 text-sm text-slate-500',
          className,
        )}
        style={{ height }}
      >
        {emptyHint}
      </div>
    );
  }

  const values = points.map((point) => valueOf(point, metric));
  const rawMax = Math.max(...values, metric === 'completion' ? 1 : 0);
  // 上限取整到"好看的刻度"，避免 y 轴出现 0.37 这种数字
  const max = rawMax <= 0 ? 1 : rawMax * 1.15;

  const padding = { top: 12, right: 12, bottom: 26, left: 40 };
  const innerW = WIDTH - padding.left - padding.right;
  const innerH = height - padding.top - padding.bottom;

  const stepX = points.length > 1 ? innerW / (points.length - 1) : 0;
  const xAt = (index: number) => padding.left + (points.length > 1 ? index * stepX : innerW / 2);
  const yAt = (value: number) => padding.top + innerH - (value / max) * innerH;

  const linePath = points
    .map((point, index) => `${index === 0 ? 'M' : 'L'} ${xAt(index).toFixed(1)} ${yAt(valueOf(point, metric)).toFixed(1)}`)
    .join(' ');

  // 面积填充：折线下方一块淡色，视觉上更容易读出"量"
  const areaPath = `${linePath} L ${xAt(points.length - 1).toFixed(1)} ${(padding.top + innerH).toFixed(1)} L ${xAt(0).toFixed(1)} ${(padding.top + innerH).toFixed(1)} Z`;

  const step = labelStep(points.length);
  const gridValues = metric === 'completion' ? [0, 0.25, 0.5, 0.75, 1] : [0, 0.5, 1];

  return (
    <div className={cn('w-full', className)}>
      <svg
        viewBox={`0 0 ${WIDTH} ${height}`}
        width="100%"
        height={height}
        role="img"
        aria-label={`趋势折线图，共 ${points.length} 个数据点`}
        preserveAspectRatio="none"
      >
        {/* 横向网格 + Y 轴刻度 */}
        {gridValues.map((ratio) => {
          const y = yAt(ratio * max);
          return (
            <g key={ratio}>
              <line
                x1={padding.left}
                x2={WIDTH - padding.right}
                y1={y}
                y2={y}
                stroke="#e2e8f0"
                strokeWidth={1}
              />
              <text x={4} y={y + 4} fontSize={11} fill="#94a3b8">
                {formatValue(ratio * max, metric)}
              </text>
            </g>
          );
        })}

        <path d={areaPath} fill="#337bff" fillOpacity={0.1} />
        <path d={linePath} fill="none" stroke="#337bff" strokeWidth={2.5} strokeLinejoin="round" />

        {/* 数据点：点多的时候只画点不画标签 */}
        {points.map((point, index) => (
          <circle
            key={`${point.bucket}-${index}`}
            cx={xAt(index)}
            cy={yAt(valueOf(point, metric))}
            r={points.length > 40 ? 1.5 : 3}
            fill="#ffffff"
            stroke="#1f5cf5"
            strokeWidth={2}
          />
        ))}

        {/* X 轴标签 */}
        {points.map((point, index) =>
          index % step === 0 || index === points.length - 1 ? (
            <text
              key={`label-${point.bucket}-${index}`}
              x={xAt(index)}
              y={height - 8}
              fontSize={11}
              fill="#94a3b8"
              textAnchor={index === 0 ? 'start' : index === points.length - 1 ? 'end' : 'middle'}
            >
              {shortBucket(point.bucket)}
            </text>
          ) : null,
        )}
      </svg>

      {/* 读屏摘要：读屏用户听不到 SVG 折线，给一份等价文本 */}
      <ul className="sr-only">
        {points.map((point, index) => (
          <li key={`sr-${point.bucket}-${index}`}>
            {point.bucket}：{formatValue(valueOf(point, metric), metric)}
          </li>
        ))}
      </ul>
    </div>
  );
}

/** '2026-03-07' → '03-07'；'2026-03' → '3月'；'2026-W10' → 'W10'。 */
function shortBucket(bucket: string): string {
  if (bucket.length === 10) return bucket.slice(5);
  if (bucket.length === 7) return `${Number(bucket.slice(5, 7))}月`;
  return bucket;
}
